import convert, { Unit } from "convert";
import {
  TCopilotNoteVital,
  TCopilotNoteVitals,
} from "../../../../../../../types/copilot";
import { HEIGHT_UNITS, TEMPERATURE_UNITS, WEIGHT_UNITS } from "./units";

type TProcessCopilotVitals = Partial<TCopilotNoteVitals>;

const normalizeBpReading = (
  measurement?: TCopilotNoteVital | number | string | null,
): TCopilotNoteVital | null => {
  if (measurement == null || measurement === "") return null;
  if (typeof measurement === "number") {
    return measurement > 0 ? { value: measurement, unit: "mmHg" } : null;
  }
  if (typeof measurement === "string") {
    const trimmed = measurement.trim();
    if (!trimmed) return null;
    const numeric = parseFloat(trimmed);
    return !isNaN(numeric) && numeric > 0
      ? { value: trimmed, unit: "mmHg" }
      : null;
  }
  const rawValue = measurement.value;
  if (rawValue == null || rawValue === "") return null;
  const numeric =
    typeof rawValue === "number" ? rawValue : parseFloat(String(rawValue));
  if (isNaN(numeric) || numeric <= 0) return null;
  return {
    value: rawValue,
    unit: measurement.unit || "mmHg",
  };
};

const hasBpValue = (
  measurement?: TCopilotNoteVital | number | string | null,
): boolean => normalizeBpReading(measurement) != null;

export function processCopilotVitals(
  value: TProcessCopilotVitals,
  existing?: Partial<TCopilotNoteVitals> | null,
) {
  const returnVal: Partial<TCopilotNoteVitals> = {};
  // Special cases are height, weight, and bloodGlucose. These should be validated.
  const isVitalValid = (vital: TCopilotNoteVital): boolean => {
    return Boolean(vital?.value && vital?.unit);
  };
  {
    // WEIGHT
    const weight = value.weight;
    const unit = value.weight?.unit?.toLowerCase();
    const normalizedWeightUnit = unit === "lbs" ? "lb" : unit;
    const valid =
      isVitalValid(weight) && WEIGHT_UNITS.includes(normalizedWeightUnit);

    if (valid) {
      const SUPPORTED_UNITS = ["kg", "lbs", "lb", "g"];

      if (!SUPPORTED_UNITS.includes(unit)) {
        const converted = convert(Number(weight.value), weight.unit as Unit).to(
          "kg",
        );
        returnVal.weight = { value: Number(converted.toFixed(1)), unit: "kg" };
      } else returnVal.weight = weight;
    }
  }

  {
    // HEIGHT
    const height = value.height;
    const unit = value.height?.unit?.toLowerCase();
    const valid = isVitalValid(height) && HEIGHT_UNITS.includes(unit);

    if (valid) {
      const SUPPORTED_UNITS = ["cm"];

      if (!SUPPORTED_UNITS.includes(unit)) {
        const converted = convert(Number(height.value), height.unit as Unit).to(
          "cm",
        );
        returnVal.height = { value: converted, unit: "cm" };
      } else returnVal.height = height;
    }
  }

  {
    // TEMPERATURE
    const temp = value.temperature;
    const unit = value.temperature?.unit?.toLowerCase().replace("°", "") ?? "";
    const valid = isVitalValid(temp) && TEMPERATURE_UNITS.includes(unit);

    if (valid) {
      const SUPPORTED_UNITS = [
        "f",
        "c",
        "fahrenheit",
        "celcius",
        "Cel",
        "degF",
      ];

      if (!SUPPORTED_UNITS.includes(unit)) {
        const converted = convert(Number(temp.value), temp.unit as Unit).to(
          "C",
        );
        returnVal.temperature = { value: `${converted}`, unit: "celcius" };
      } else
        returnVal.temperature = {
          value: `${temp.value}`,
          unit: unit === "c" ? "celcius" : unit === "f" ? "fahrenheit" : unit,
        };
    }
  }

  {
    // BLOOD GLUCOSE
    const bg = value.bloodGlucose;
    const hasValue = Boolean(Number(bg?.value));
    const hasValidUnitOrEmpty =
      !bg?.unit || ["mmol/L", "mg/dL"].includes(bg?.unit);

    if (hasValue && hasValidUnitOrEmpty) {
      returnVal.bloodGlucose = { value: bg.value, unit: bg.unit ?? "mg/dL" };
    }
  }

  {
    // BLOOD PRESSURE
    const bp = value.bloodPressure;
    const existingBp = existing?.bloodPressure;
    const systolic =
      normalizeBpReading(bp?.systolic) ??
      normalizeBpReading(existingBp?.systolic);
    const diastolic =
      normalizeBpReading(bp?.diastolic) ??
      normalizeBpReading(existingBp?.diastolic);

    if (systolic && diastolic) {
      returnVal.bloodPressure = { systolic, diastolic };
    }
  }

  {
    // PULSE
    const pulse = value.pulse;
    const hasValue = Boolean(Number(pulse?.value));
    const hasValidUnitOrEmpty =
      !pulse?.unit || ["/min", "bpm"].includes(pulse.unit);

    if (hasValue && hasValidUnitOrEmpty) {
      returnVal.pulse = {
        value: pulse.value,
        unit: pulse.unit === "bpm" ? "/min" : (pulse.unit ?? "/min"),
      };
    }
  }

  {
    // RESPIRATORY RATE
    const rr = value.respiratoryRate;
    const hasValue = Boolean(Number(rr?.value));
    const rrUnit = rr?.unit?.toLowerCase();
    const hasValidUnitOrEmpty =
      !rr?.unit ||
      rrUnit === "/min" ||
      rrUnit === "breaths/min" ||
      rrUnit === "breaths/minute";

    if (hasValue && hasValidUnitOrEmpty) {
      returnVal.respiratoryRate = { value: rr.value, unit: "/min" };
    }
  }

  {
    // OXYGEN SATURATION
    const os = value.oxygenSaturation;
    const hasValue = Boolean(Number(os?.value));
    const hasValidUnitOrEmpty = !os?.unit || os?.unit === "%";

    if (hasValue && hasValidUnitOrEmpty) {
      returnVal.oxygenSaturation = {
        value: Number(Number(os.value).toFixed(1)),
        unit: os.unit ?? "%",
      };
    }
  }

  {
    // WAIST CIRCUMFERENCE
    const waist = value.waist;
    const unit = waist?.unit?.toLowerCase();
    const hasValue = Boolean(Number(waist?.value));
    const hasValidUnitOrEmpty =
      !unit || ["cm", "in", "inch", "inches"].includes(unit);

    if (hasValue && hasValidUnitOrEmpty) {
      if (unit === "in" || unit === "inch" || unit === "inches") {
        const converted = convert(Number(waist.value), "in").to("cm");
        returnVal.waist = {
          value: Number(converted.toFixed(1)),
          unit: "cm",
        };
      } else {
        returnVal.waist = { value: waist.value, unit: "cm" };
      }
    }
  }

  {
    // PAIN - simple TCopilotNoteVital structure like other vitals
    const pain = value.pain;
    const hasValue = Boolean(Number(pain?.value));

    if (hasValue) {
      // Pain scale should be integer 0-10, no decimals
      const painValue = Math.round(Number(pain.value));
      // Ensure pain is within valid range (0-10)
      const clampedPainValue = Math.max(0, Math.min(10, painValue));

      returnVal.pain = {
        value: clampedPainValue,
        // unit can store duration info if needed, or be empty
        unit: pain.unit || undefined,
      };
    }
  }

  return returnVal;
}

/** Returns true when the user entered an incomplete blood pressure reading. */
export function hasIncompleteBloodPressure(
  vitals?: Partial<TCopilotNoteVitals> | null,
): boolean {
  const bp = normalizeBPForValidation(vitals?.bloodPressure);
  const hasSystolic = bp.systolic.trim() !== "";
  const hasDiastolic = bp.diastolic.trim() !== "";
  return (hasSystolic || hasDiastolic) && !(hasSystolic && hasDiastolic);
}

function normalizeBPForValidation(bp: TCopilotNoteVitals["bloodPressure"]) {
  if (!bp) return { systolic: "", diastolic: "" };
  const read = (measurement?: TCopilotNoteVital | number | string | null) => {
    if (measurement == null || measurement === "") return "";
    if (typeof measurement === "number" || typeof measurement === "string") {
      return String(measurement).trim();
    }
    return measurement.value == null ? "" : String(measurement.value).trim();
  };
  return {
    systolic: read(bp.systolic),
    diastolic: read(bp.diastolic),
  };
}

export { hasBpValue };

type VitalsPopulateOutcome = {
  created?: boolean;
  updated?: boolean;
  skipped?: boolean;
  unchanged?: boolean;
};

function resolveVitalsPopulateOutcome(result: {
  data?: VitalsPopulateOutcome;
  created?: boolean;
  updated?: boolean;
  skipped?: boolean;
  unchanged?: boolean;
  message?: string;
}): VitalsPopulateOutcome {
  if (result?.data) {
    return result.data;
  }

  return {
    created: result.created,
    updated: result.updated,
    skipped: result.skipped ?? result.unchanged,
    unchanged: result.unchanged ?? result.skipped,
  };
}

export function getVitalsPopulateNotification(result: {
  data?: VitalsPopulateOutcome;
  created?: boolean;
  updated?: boolean;
  skipped?: boolean;
  unchanged?: boolean;
  message?: string;
}): { message: string; variant: "success" | "warning" | "error" } {
  const outcome = resolveVitalsPopulateOutcome(result);
  if (outcome.updated) {
    return {
      message: "Vitals updated on the encounter",
      variant: "success",
    };
  }
  if (outcome.created) {
    return {
      message: "Vitals populated successfully",
      variant: "success",
    };
  }
  if (outcome.skipped || outcome.unchanged) {
    return {
      message: "Vitals already match the note; left unchanged",
      variant: "warning",
    };
  }
  return {
    message: result?.message || "No valid vitals found in the note to apply",
    variant: "warning",
  };
}
